import { useAuth } from '@clerk/react';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import {
  deletePortalFile,
  uploadPortalFile,
  type CustomerFileOut,
} from '@/api/portalFiles';
import { UploadGuidancePanel } from '@/components/UploadGuidancePanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { UploadCategoryMeta } from '@/lib/portalOnboarding';

interface PerFileState {
  filename: string;
  status: 'uploading' | 'error';
  error?: string;
}

/** One onboarding upload section, parameterized by category. Generalized
 *  from the original invoice-only CustomerInvoiceUpload. The parent owns
 *  the file query (so it can derive per-category counts for the
 *  still-missing banner) and passes this section its filtered `items`
 *  plus an `onChanged` callback to invalidate after upload/delete. */
export function CustomerUploadSection({
  engagementId,
  meta,
  items,
  onChanged,
}: {
  engagementId: string;
  meta: UploadCategoryMeta;
  items: CustomerFileOut[];
  onChanged: () => void;
}) {
  const { getToken } = useAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [inFlight, setInFlight] = useState<PerFileState[]>([]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const token = (await getToken()) ?? '';
      return uploadPortalFile(engagementId, file, token, meta.category);
    },
  });

  const remove = useMutation({
    mutationFn: async (fileId: string) => {
      const token = (await getToken()) ?? '';
      return deletePortalFile(engagementId, fileId, token);
    },
    onSuccess: onChanged,
  });

  const maxMb = Math.round(meta.maxBytes / (1024 * 1024));
  const acceptAttr = meta.extensions ? meta.extensions.map((e) => `.${e}`).join(',') : undefined;

  const acceptFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    Array.from(list).forEach((file) => {
      const ext = file.name.includes('.') ? (file.name.split('.').pop()?.toLowerCase() ?? '') : '';
      if (meta.extensions && !meta.extensions.includes(ext)) {
        setInFlight((s) => [
          ...s,
          { filename: file.name, status: 'error', error: 'Unsupported file type' },
        ]);
        return;
      }
      if (file.size > meta.maxBytes) {
        setInFlight((s) => [
          ...s,
          { filename: file.name, status: 'error', error: `File exceeds ${maxMb} MB` },
        ]);
        return;
      }
      setInFlight((s) => [...s, { filename: file.name, status: 'uploading' }]);
      upload
        .mutateAsync(file)
        .then(() => {
          setInFlight((s) => s.filter((row) => row.filename !== file.name));
          onChanged();
        })
        .catch((err: unknown) => {
          const detail =
            err instanceof ApiError ? `HTTP ${err.status}` : 'Upload failed — see console.';
          setInFlight((s) =>
            s.map((row) =>
              row.filename === file.name ? { ...row, status: 'error', error: detail } : row,
            ),
          );
        });
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>{meta.label}</CardTitle>
          <span className="text-xs text-brand-slate">
            {items.length === 0
              ? 'Nothing yet'
              : `${items.length} file${items.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <CardDescription>
          {meta.description} Up to {maxMb} MB each.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <UploadGuidancePanel category={meta.category} />
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            acceptFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Upload ${meta.label} files`}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-6 py-8 text-center text-sm transition-colors ${
            isDragging
              ? 'border-brand-royal bg-brand-royal/5 text-brand-navy'
              : 'border-border text-brand-slate hover:border-brand-royal/50'
          }`}
        >
          <p className="font-medium text-brand-navy">Drag and drop files here</p>
          <p className="mt-1 text-xs">or click to pick from your computer</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={acceptAttr}
            className="hidden"
            onChange={(e) => acceptFiles(e.target.files)}
          />
        </div>

        {inFlight.length > 0 && (
          <ul className="grid gap-1 text-xs">
            {inFlight.map((row, i) => (
              <li key={`${row.filename}-${i}`} className="flex items-center gap-2">
                <span className="font-mono">{row.filename}</span>
                <span
                  className={row.status === 'uploading' ? 'text-brand-slate' : 'text-destructive'}
                >
                  {row.status === 'uploading' ? 'uploading…' : row.error}
                </span>
                {row.status === 'error' && (
                  <button
                    type="button"
                    onClick={() => setInFlight((s) => s.filter((_, idx) => idx !== i))}
                    className="ml-auto text-brand-slate underline-offset-4 hover:underline"
                  >
                    dismiss
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {items.length === 0 ? (
          <p className="text-xs text-brand-slate">No files uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {items.map((f) => (
              <UploadedRow
                key={f.id}
                file={f}
                onDelete={() => remove.mutate(f.id)}
                deleting={remove.isPending && remove.variables === f.id}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function UploadedRow({
  file,
  onDelete,
  deleting,
}: {
  file: CustomerFileOut;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <span className="font-medium text-brand-navy">{file.filename}</span>
      <span className="text-xs text-brand-slate">{formatBytes(file.byte_count)}</span>
      <span className="ml-auto text-xs text-brand-slate">
        {new Date(file.uploaded_at).toLocaleString()}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onDelete}
        disabled={deleting}
        className="text-destructive hover:text-destructive"
      >
        {deleting ? 'Removing…' : 'Remove'}
      </Button>
    </li>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
